<template>
  <!--
    App shell (Super Admins by default, office users per STAGE.nav): sidebar 1b / iPad rail 1g + top bar 1d / phone bar 1h.
    Layout.vue renders this INSTEAD of the classic <Sidebar/> + <Header/> (the content column is
    shared). It owns everything the presentational children cannot: which popover is open, the
    viewport mode, collapse, the office's sidebar arrangement and its save, counts / dots, and the
    side effects the classic Header / Sidebar ran on mount (see the script header).
  -->
  <div
    class="app-shell d-print-none"
    :class="['app-shell--' + mode, { 'app-shell--collapsed': frame.collapsed }]"
    :style="{ '--shell-left': shellLeft }"
    data-test="app-shell"
  >
    <!-- Header.vue's red ANNOUNCEMENT bar (Firestore notifications of type ANNOUNCEMENT). -->
    <div v-if="announcement" class="app-shell__announcement" data-test="app-shell-announcement">
      <span v-html="announcement.text" />
      <a href="#" class="app-shell__announcement-logout" @click.prevent="onLogout">Log Out</a>
    </div>

    <AppSidebar
      v-if="showSidebar"
      :items="items"
      :pinned="layout.pinned"
      :more="layout.more"
      :active-id="activeId"
      :variant="sidebarVariant"
      :can-edit="canEdit"
      :more-open="moreSectionOpen"
      :flyout-open="openPanel === 'more'"
      :help-link="helpLink"
      :touch="mode === 'tablet'"
      :is-default="layoutIsDefault"
      @select="onSelectApp"
      @reorder="onReorder"
      @pin="onPin"
      @unpin="onUnpin"
      @toggle-collapsed="onToggleCollapsed"
      @toggle-more="moreSectionOpen = !moreSectionOpen"
      @open-flyout="openPanelAs('more')"
      @close-flyout="onCloseFlyout"
      @reset-layout="onResetLayout"
    />

    <AppTopBar
      ref="topBar"
      :mode="mode"
      :page-title="pageTitle"
      :mask-on="maskOn"
      :unread-count="unreadNotifications"
      :alerts-prompt="alertsPrompt"
      :account="account"
      :office="office"
      :open-panel="topBarPanel"
      :search-props="searchProps"
      :can-my-account="canMyAccount"
      :can-settings="canSettings"
      :can-switch="canSwitchSites"
      :super-admin="superAdmin"
      :office-switch="officeSwitch"
      @open="onTopBarOpen"
      @close="closePanel({ restoreFocus: true })"
      @menu="onMenu"
      @pick-page="onPickPage"
      @pick-patient="onPickPatient"
      @pick-task="onPickTask"
      @refresh-patients="refreshRoster"
      @my-account="onMyAccount"
      @settings="onSettings"
      @switch-site="onSwitchSite"
      @toggle-mask="onToggleMask"
      @logout="onLogout"
      @classic-nav="onClassicNav"
      @office-switch="onOfficeSwitch"
      @open-tasks="onOpenTasks"
      @alerts-enable="$emit('alerts-enable')"
      @alerts-not-now="$emit('alerts-not-now')"
      @alerts-sound-replay="$emit('alerts-sound-replay')"
      @alerts-sound-dismiss="$emit('alerts-sound-dismiss')"
    />

    <!-- Phone (1h): the first four pinned apps + More along the bottom; ☰ / More open the drawer. -->
    <AppTabBar
      v-if="mode === 'phone'"
      :items="tabItems"
      :active-id="activeId"
      :more-active="tabMoreActive"
      @select="onSelectApp"
      @more="openPanelAs('drawer')"
    />
    <AppDrawer
      v-if="mode === 'phone'"
      :open="openPanel === 'drawer'"
      :items="items"
      :pinned="layout.pinned"
      :more="layout.more"
      :active-id="activeId"
      :account="account"
      :office="office"
      :can-edit="canEdit"
      :is-default="layoutIsDefault"
      :can-my-account="canMyAccount"
      :can-settings="canSettings"
      :can-switch="canSwitchSites"
      :super-admin="superAdmin"
      :office-switch="officeSwitch"
      :mask-on="maskOn"
      @close="onDrawerClose"
      @search="onDrawerSearch"
      @select="onSelectApp"
      @reorder="onReorder"
      @pin="onPin"
      @unpin="onUnpin"
      @reset-layout="onResetLayout"
      @my-account="onMyAccount"
      @settings="onSettings"
      @switch-site="onSwitchSite"
      @toggle-mask="onToggleMask"
      @logout="onLogout"
      @classic-nav="onClassicNav"
      @office-switch="onOfficeSwitch"
    />

    <!-- The ONE outside-click catcher: transparent, under the chrome ($shell-z-scrim < chrome). -->
    <div
      v-if="scrimOn"
      class="app-shell__scrim"
      data-test="app-shell-scrim"
      aria-hidden="true"
      @click="closePanel()"
    />

    <AppSwitchSiteModal
      :open="switchSiteOpen"
      @close="switchSiteOpen = false"
      @multiple-sites="canSwitchSites = $event"
      @sites="onSites"
    />
  </div>
</template>

<script>
/**
 * App shell — the frame (plan: ~/.claude/plans/nested-conjuring-flurry.md §3–§6; resume block in
 * Documents/Admin-data-table-redesign.md §0). Gate + mount: Layout.vue (`navUseNewShell`).
 *
 *   emits  frame({ mode, collapsed }) on mount and on every change — Layout offsets `.wrap`;
 *          nav-ui('old') — "Classic navigation" (Layout writes teamio_nav_ui and swaps the chrome);
 *          alerts-enable / alerts-not-now / alerts-sound-replay / alerts-sound-dismiss — the bell's
 *          "turn on desktop alerts" row; Layout runs them (the same methods the classic cards use).
 *
 * Office users (never a Super Admin) get ONE "Switch to classic Teamio" / "Try the new Teamio" item in
 * the account menu and the phone drawer (`officeSwitch` = uiVersion.js officeSwitchState). Its click
 * is handled here by uiSwitch.js switchOfficeUi: store the choice under the employee ID, count it,
 * reload. A reload (not a swap in place) is on purpose — it avoids the duplicate notification
 * listeners this shell and the classic Header would otherwise leave behind.
 *
 * Phone (N4): AppTabBar = `tabBarIds(layout)`; ☰ (onMenu) and the tab bar's More open AppDrawer
 * (`openPanel === 'drawer'`, its own scrim at $shell-z-drawer, so `scrimOn` skips it). Drawer edits
 * go through the same onReorder / onPin / onUnpin / onResetLayout → applyLayout pipeline; its
 * search hands off to the full-screen phoneSearch inside the same tap. Route and mode changes close
 * it like any other panel.
 *
 * ⛔ CLASSIC SIDE EFFECTS. Under this shell the classic Header.vue / Sidebar.vue are NOT mounted, so
 *    everything they did on load that other pages rely on is repeated here (each names its source):
 *      Header init()  notifications listener, empty-roster self-heal, chat-unread listener,
 *                     patient/employee "needs updating" listener (+ the two watchers that refresh);
 *      Header         recent-patient tracking ($route watcher, immediate), ANNOUNCEMENT bar;
 *      Sidebar        chat + SMS sound / push alerts, tab title + favicon for unread chat / SMS.
 *    Settings / employees / CASL rules are loaded by Layout's own mixin created() — not repeated.
 *    The site list + `userSites` cache refresh live in AppSwitchSiteModal (mounted always).
 *
 * Deliberately NOT src/mixin.js: its created() would re-run the settings / employees / rules boot
 * Layout already ran. Store reads use their own names (shellUser, shellSiteID, …).
 */
import Push from 'push.js';
import AppSidebar from './AppSidebar.vue';
import AppTopBar from './AppTopBar.vue';
import AppSwitchSiteModal from './AppSwitchSiteModal.vue';
import AppTabBar from './AppTabBar.vue';
import AppDrawer from './AppDrawer.vue';
import topmixin from '@/topmixin.js';
import CommonQueries from '@/sql/queries';
import { NAV_APPS, HELP_LINK, PAGE_ALIASES, visibleApps, activeAppId } from '@/utils/nav/apps';
import {
  LAYOUT_SETTING, parseLayout, resolveLayout, serializeLayout, pinApp, unpinApp,
  isDefaultLayout, canEditLayout, readCollapsed, writeCollapsed, tabBarIds,
} from '@/utils/nav/layout';
import { buildPages, buildCommands, taskSub } from '@/utils/nav/search';
import { tasksForView, rowMeta } from '@/utils/tasks/myDay';
import { shouldLoadSearchTasks, tasksSearchStatus } from '@/utils/tasks/searchLoad';
import { DESKTOP_QUERY, PHONE_QUERY, shellModeFor } from '@/utils/nav/breakpoints';
import { browserStorage, officeSwitchState } from '@/utils/followups/uiVersion';
import { switchOfficeUi } from '@/utils/followups/uiSwitch';
import { visibleTabs } from '@/utils/followups/pageChrome';
import { getRecentPatNums } from '@/utils/recentPatients';
import { isEnabled, getSound } from '@/utils/notificationPrefs';
import { canUseConversations } from '@/utils/siteCapability';
import {
  navigate, gotoPatient, goToSettings, goToMyAccount, logout, refreshPatients, isMaskOn,
  confirmToggleMask, trackRecentPatient, currentSiteName, siteDateFormat, unreadConversationsCount,
} from './shellActions';

/** Debounce between the last drag / pin and the save (plan §5). */
export const SAVE_DELAY_MS = 800;

function initialMode() {
  try {
    if (typeof window === 'undefined' || !window.matchMedia) return 'desktop';
    return shellModeFor(window.matchMedia(DESKTOP_QUERY).matches === true, window.matchMedia(PHONE_QUERY).matches === true);
  } catch (e) {
    return 'desktop';
  }
}

/**
 * The draft keeps the stored ids THIS user cannot see (another role's app, e.g. Schedule for a
 * Super Admin when an Admin saves), each on its stored side, so one person's save never drops
 * another person's pinned app.
 */
export function keepHiddenIds(draft, stored, visibleIds) {
  const out = { pinned: draft.pinned.slice(), more: draft.more.slice() };
  if (!stored) return out;
  const visible = {};
  (visibleIds || []).forEach((id) => { visible[id] = true; });
  const seen = {};
  out.pinned.concat(out.more).forEach((id) => { seen[id] = true; });
  ['pinned', 'more'].forEach((side) => {
    (stored[side] || []).forEach((id) => {
      if (!visible[id] && !seen[id]) {
        seen[id] = true;
        out[side].push(id);
      }
    });
  });
  return out;
}

export default {
  name: 'AppShell',
  components: { AppSidebar, AppTopBar, AppSwitchSiteModal, AppTabBar, AppDrawer },

  props: {
    // The "turn on desktop alerts" prompt Layout owns: { phase: 'ask'|'sound'|'', result }. Drawn as a
    // dot on the bell and a row in its popover (utils/notificationPromptCopy.js has the words).
    alertsPrompt: { type: Object, default: () => ({ phase: '', result: '' }) },
  },

  data() {
    return {
      // 'desktop' | 'tablet' | 'phone' (DESKTOP_QUERY / PHONE_QUERY).
      mode: initialMode(),
      // Collapse-to-icons, per person (localStorage teamio_nav_collapsed); desktop only.
      collapsedPref: readCollapsed(browserStorage()),
      // null | 'search' | 'notifications' | 'account' | 'more' | 'drawer' | 'phoneSearch'
      openPanel: null,
      // Wide sidebar: the More apps section expanded.
      moreSectionOpen: true,
      switchSiteOpen: false,
      canSwitchSites: false,
      officeIndex: 0,
      officeCount: 0,
      recentPatNums: [],
      patientsRefreshing: false,
      // In-memory only (no server timestamp exists): when THIS session last reloaded the roster.
      lastSyncedAt: null,
      // Optimistic arrangement while a save is pending / in flight (plan §5).
      layoutDraft: null,
      layoutDirty: false,
    };
  },

  computed: {
    // Demo session (customer/isDemo): no classic-Teamio prompts, the demo shows the new Teamio only (Saumil 8a).
    // Store-safe: component tests mount without a store or without getters.
    viewerIsDemo() {
      const store = this.$store;
      return !!(store && store.getters && store.getters['customer/isDemo'] === true);
    },
    // ---- store reads (own names: this is not src/mixin.js) ----------------------------------
    shellUser() {
      return this.$store.state.customer.user;
    },
    shellUserID() {
      return this.$store.state.customer.userID;
    },
    shellSiteID() {
      return this.$store.state.customer.siteID;
    },
    shellSettings() {
      const s = this.$store.state.customer.settings;
      return Array.isArray(s) ? s : [];
    },
    settingsMap() {
      const map = {};
      this.shellSettings.forEach((row) => {
        if (row && row.Property) map[row.Property] = row;
      });
      return map;
    },
    patientNeedsUpdating() {
      return this.$store.state.customer.patientNeedsUpdating;
    },
    employeeNeedsUpdating() {
      return this.$store.state.customer.employeeNeedsUpdating;
    },
    chatState() {
      return this.$store.state.chat || {};
    },
    conversationsState() {
      return this.$store.state.conversationsV2 || {};
    },
    newChatMessagesAvailable() {
      return this.chatState.newChatMessagesAvailable === true;
    },
    newChatMessageTimestamp() {
      return this.chatState.newChatMessageTimestamp;
    },
    newConversationMsgTimestamp() {
      return this.conversationsState.newConversationMsgTimestamp;
    },
    notificationsList() {
      const n = this.$store.state.notifications;
      return n && Array.isArray(n.notifications) ? n.notifications : [];
    },

    superAdmin() {
      return !!(this.shellUser && this.shellUser.Role === 'Super Admin');
    },
    // { visible, target } for the office users' single classic / new switch (never visible for a
    // Super Admin, nor in the demo). localStorage is not reactive, but an office switch reloads the page.
    officeSwitch() {
      return officeSwitchState({ isSuperAdmin: this.superAdmin, storage: browserStorage(), userKey: this.shellUserID, isDemo: this.viewerIsDemo === true });
    },
    role() {
      return (this.shellUser && this.shellUser.Role) || '';
    },
    canEdit() {
      return canEditLayout(this.role);
    },

    // ---- apps ---------------------------------------------------------------------------------
    canFn() {
      return (action, subject) => {
        try {
          return typeof this.$can === 'function' ? !!this.$can(action, subject) : false;
        } catch (e) {
          return false;
        }
      };
    },
    // The classic sidebar's two flags come from topmixin.js — the SAME functions, run on our state.
    navFlags() {
      const ctx = { user: this.shellUser, settings: this.shellSettings, $can: this.canFn };
      return {
        isSuperAdmin: this.superAdmin,
        canAccessTxPresenter: topmixin.computed.canAccessTxPresenter.call(ctx) === true,
        showInsuranceVerificationNav: topmixin.computed.showInsuranceVerificationNav.call(ctx) === true,
      };
    },
    apps() {
      return visibleApps(NAV_APPS, { can: this.canFn, flags: this.navFlags });
    },
    visibleIds() {
      return this.apps.map(a => a.id);
    },

    // ---- counts + dots (Header / Sidebar logic, copied) -----------------------------------------
    tasksDueTodayCount() {
      const list = this.$store.getters['tasks/allMyTasksDueToday'];
      return Array.isArray(list) ? list.length : 0;
    },
    // Sidebar.vue newConversationMsgAvailable: per-site capability (store allSettings OR the
    // settings array), then any room with unread > 0.
    conversationsEnabled() {
      const customer = this.$store.state.customer || {};
      return canUseConversations(customer.allSettings) || canUseConversations(customer.settings);
    },
    rooms() {
      const r = this.conversationsState.rooms;
      return Array.isArray(r) ? r : [];
    },
    smsUnreadCount() {
      return this.conversationsEnabled ? unreadConversationsCount(this.rooms) : 0;
    },
    newConversationMsgAvailable() {
      if (!this.conversationsEnabled) return false;
      return this.rooms.some(room => (parseInt(room && room.unreadCount, 10) || 0) > 0);
    },
    // Sidebar.vue hasKanbanUnread.
    hasKanbanUnread() {
      return this.notificationsList.some(n => n && n.type === 'KANBAN');
    },
    items() {
      return this.apps.map((a) => {
        const row = Object.assign({}, a, { count: null, dot: false });
        if (a.countKey === 'tasks') row.count = this.tasksDueTodayCount;
        if (a.countKey === 'sms') {
          row.count = this.smsUnreadCount;
          row.dot = this.newConversationMsgAvailable;
        }
        if (a.dot === 'kanban') row.dot = this.hasKanbanUnread;
        if (a.dot === 'chat') row.dot = this.newChatMessagesAvailable;
        return row;
      });
    },
    helpLink() {
      return HELP_LINK;
    },

    // ---- arrangement (plan §5) ------------------------------------------------------------------
    storedLayout() {
      const row = this.settingsMap[LAYOUT_SETTING];
      return parseLayout(row ? row.PropertyValue : null);
    },
    layout() {
      const source = this.layoutDirty && this.layoutDraft ? this.layoutDraft : this.storedLayout;
      return resolveLayout(source, this.visibleIds);
    },
    layoutIsDefault() {
      return isDefaultLayout(this.layout, this.visibleIds);
    },

    // ---- frame ----------------------------------------------------------------------------------
    activeId() {
      return activeAppId(this.items, this.$route ? this.$route.path : '');
    },
    activeItem() {
      return this.items.find(i => i.id === this.activeId) || null;
    },
    pageTitle() {
      return this.activeItem ? this.activeItem.label : '';
    },
    showSidebar() {
      return this.mode !== 'phone';
    },
    sidebarVariant() {
      if (this.mode !== 'desktop') return 'rail';
      return this.collapsedPref ? 'icons' : 'wide';
    },
    frame() {
      return { mode: this.mode, collapsed: this.mode === 'desktop' && this.collapsedPref === true };
    },
    // The top bar's `left` (and anything else sized off the sidebar) reads this CSS variable.
    shellLeft() {
      if (this.mode === 'phone') return '0px';
      return this.sidebarVariant === 'wide' ? '240px' : '64px';
    },
    // The top bar knows search / notifications / account; 'more' and 'drawer' are not its panels.
    topBarPanel() {
      const p = this.openPanel;
      return p === 'search' || p === 'phoneSearch' || p === 'notifications' || p === 'account' ? p : null;
    },
    // Phone tab bar: the office's first four pinned apps (rows this person can see).
    tabItems() {
      const byId = {};
      this.items.forEach((row) => { byId[row.id] = row; });
      return tabBarIds(this.layout).map(id => byId[id]).filter(Boolean);
    },
    // "More" is lit when the page is not one of the four, or while the drawer is open (design H).
    tabMoreActive() {
      if (this.openPanel === 'drawer') return true;
      return !this.tabItems.some(row => row.id === this.activeId);
    },
    scrimOn() {
      // phoneSearch is a full-screen sheet; the drawer (N4) brings its own scrim.
      return !!this.openPanel && this.openPanel !== 'phoneSearch' && this.openPanel !== 'drawer';
    },

    // ---- top bar --------------------------------------------------------------------------------
    account() {
      const u = this.shellUser || {};
      return { name: u.Name || '', role: u.Role || '', avatarUrl: u.picture || null };
    },
    office() {
      return { name: currentSiteName(this) || '', index: this.officeIndex, count: this.officeCount };
    },
    canMyAccount() {
      return this.canFn('view', 'myaccount');
    },
    // Header.vue isAdminOrAbove.
    canSettings() {
      return this.role === 'Super Admin' || this.role === 'Admin';
    },
    maskOn() {
      return isMaskOn();
    },
    unreadNotifications() {
      const n = this.$store.getters['notifications/count'];
      return typeof n === 'number' ? n : 0;
    },
    announcement() {
      return this.notificationsList.find(n => n && n.type === 'ANNOUNCEMENT') || null;
    },
    pages() {
      return buildPages({
        apps: this.apps,
        followupTabs: visibleTabs(this.canFn, {}),
        canMyAccount: this.canMyAccount,
        canSettings: this.canSettings,
        aliases: PAGE_ALIASES,
      });
    },
    // The Tasks commands (New task, Task calendar …) — only where the shell shows Tasks.
    commands() {
      return buildCommands({ canTasks: this.apps.some(a => a && a.id === 'tasks') });
    },
    searchOpenNow() {
      return this.topBarPanel === 'search' || this.topBarPanel === 'phoneSearch';
    },
    // The search's inputs for starting its one-time read of the open tasks (see watch.searchOpenNow).
    searchLoadState() {
      const t = this.$store.state.tasks || {};
      return {
        open: this.searchOpenNow,
        canTasks: this.commands.length > 0,
        siteID: this.shellSiteID,
        tasksLoaded: t.tasksLoaded,
        loading: t.searchTasksLoading,
        loaded: t.searchTasksLoaded,
        loadedSiteID: t.searchTasksSiteID,
      };
    },
    // 'ready' | 'loading' | 'failed' — what the search says about tasks.
    tasksStatus() {
      const t = this.$store.state.tasks || {};
      return tasksSearchStatus({
        tasksLoaded: t.tasksLoaded, loaded: t.searchTasksLoaded, loadedSiteID: t.searchTasksSiteID, siteID: this.shellSiteID, failed: t.searchTasksFailed,
      });
    },
    /**
     * The tasks the search can find: the Tasks page's live lists once it has loaded them, else the
     * search's own one-time read (tasks/getTasksForSearch). The search never starts the page's listener.
     * Built only while the search is open, soonest due first, undated last. `hay` carries tagged
     * patient names so "smith" finds a task about Smith.
     */
    paletteTasks() {
      if (!this.searchOpenNow || !this.commands.length) return [];
      const t = this.$store.state.tasks || {};
      const live = t.tasksLoaded === true;
      const read = !live && this.tasksStatus === 'ready' ? t.searchTasks : [];
      const d = new Date();
      const pad = n => (n < 10 ? '0' : '') + n;
      const today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const ctx = { userID: this.shellUserID, today };
      const nameById = {};
      ((this.$store.state.customer && this.$store.state.customer.employees) || []).forEach((e) => {
        if (e && e.EmployeeID !== undefined) nameById[String(e.EmployeeID)] = e.Name;
      });
      const rows = tasksForView('everyone', {
        myCreated: t.myCreatedTasks, assignedToMe: t.assignedToMeTasks, myEmployees: t.myEmployeesTasks, allSite: live ? t.allTasksForSite : read,
      }, ctx).map((task) => {
        const m = rowMeta(task, ctx);
        return {
          id: task.id,
          text: m.text,
          sub: taskSub(m.dueDay, today, (m.assigneeEmpIDs || []).map(id => nameById[String(id)])),
          hay: (m.patients || []).map(p => p && p.name).filter(Boolean).join(' '),
          due: m.dueDay || '9999-12-31',
        };
      });
      rows.sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0));
      return rows;
    },
    kbdHint() {
      try {
        const p = (typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '';
        return /Mac|iPhone|iPad/.test(p) ? '⌘K' : 'Ctrl K';
      } catch (e) {
        return 'Ctrl K';
      }
    },
    searchProps() {
      const patients = this.$store.state.customer.patients;
      return {
        patients: Array.isArray(patients) ? patients : [],
        recentPatNums: this.recentPatNums,
        pages: this.pages,
        commands: this.commands,
        tasks: this.paletteTasks,
        tasksStatus: this.tasksStatus,
        dateFormat: siteDateFormat(this),
        refreshing: this.patientsRefreshing,
        lastSyncedAt: this.lastSyncedAt,
        kbd: this.kbdHint,
      };
    },
  },

  watch: {
    // Header.vue: it outlives the fullPath-keyed <router-view>, so it sees every profile visit —
    // so does the shell. Any navigation also closes the open popover.
    $route: {
      immediate: true,
      handler(to, from) {
        const next = trackRecentPatient(this.shellSiteID, to);
        if (next) this.recentPatNums = next;
        if (from) this.closePanel();
      },
    },

    shellSiteID() {
      this.recentPatNums = getRecentPatNums(this.shellSiteID);
    },

    // Tasks from any page: the first time the search opens before the Tasks page has loaded them, ONE read
    // of the open tasks (no listener, no loading overlay). A failed read is tried again at the next open.
    searchOpenNow() {
      if (shouldLoadSearchTasks(this.searchLoadState)) this.safeDispatch('tasks/getTasksForSearch', { siteID: this.shellSiteID });
    },

    // Header.vue patientNeedsUpdating / employeeNeedsUpdating (the listener started in boot()).
    patientNeedsUpdating(value) {
      if (!value) return;
      this.refreshRoster();
      this.safeDispatch('customer/removePatientNeedsUpdating', { siteID: this.shellSiteID });
    },
    employeeNeedsUpdating(value) {
      if (!value) return;
      this.safeDispatch('customer/getEmployees', { sqlQuery: CommonQueries.getEmployees(this.shellSiteID) });
      this.safeDispatch('customer/removeEmployeeNeedsUpdating', { siteID: this.shellSiteID });
    },

    frame(f) {
      this.$emit('frame', f);
    },

    mode() {
      this.closePanel();
    },

    // ---- Sidebar.vue alerts (copied; the classic sidebar is not mounted under this shell) --------
    newChatMessagesAvailable(value) {
      const link = document.querySelector("[rel='icon']");
      if (value) {
        document.title = '👀 Teamio - New Chat Message!';
        if (link) link.setAttribute('href', '/alert.ico');
      } else {
        document.title = 'Teamio.app';
        if (link) link.setAttribute('href', 'favicon.ico');
      }
    },

    // Every new chat snapshot (not just the boolean flip). Gated per user (notificationPrefs).
    newChatMessageTimestamp(value) {
      if (!(value > 0)) return;
      const userID = this.shellUserID;
      if (!isEnabled(userID, 'chat')) return;
      this.playAlertSound('/sounds/' + getSound(userID, 'chat'));
      const msg = this.chatState.latestUnreadMessage;
      try {
        if (msg && msg.senderName) {
          const content = msg.content || '';
          const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;
          Push.create(msg.senderName, { body: preview, timeout: 6000 });
        } else {
          Push.create('New Teamio Chat Message Available!', { timeout: 6000 });
        }
      } catch (e) { /* Push throws when permission is denied */ }
    },

    // Every inbound SMS. Debounced 650ms: the backend writes the conversation doc in two phases
    // (tsNum first, content ~500ms later), which would otherwise push twice — the first stale.
    newConversationMsgTimestamp(value) {
      if (!(value > 0)) return;
      const userID = this.shellUserID;
      if (!isEnabled(userID, 'sms')) return;
      if (this.smsPushTimer) clearTimeout(this.smsPushTimer);
      this.smsPushTimer = setTimeout(() => {
        this.smsPushTimer = null;
        this.playAlertSound('/sounds/' + getSound(userID, 'sms'));
        const msg = this.conversationsState.latestInboundMessage;
        try {
          if (msg && msg.roomName) {
            const content = msg.content || '';
            const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;
            Push.create(`SMS from ${msg.roomName}`, { body: preview, timeout: 6000 });
          } else {
            Push.create('New Teamio SMS Message!', { timeout: 6000 });
          }
        } catch (e) { /* Push throws when permission is denied */ }
      }, 650);
    },

    // Chat wins the tab title: SMS only touches it when no chat alert is showing.
    newConversationMsgAvailable(value) {
      if (this.newChatMessagesAvailable) return;
      const link = document.querySelector("[rel='icon']");
      if (value) {
        if (!document.title.includes('Chat Message')) {
          document.title = '📱 Teamio - New SMS Messages!';
          if (link) link.setAttribute('href', '/alert.ico');
        }
      } else {
        document.title = 'Teamio.app';
        if (link) link.setAttribute('href', '/favicon.ico');
      }
    },
  },

  created() {
    // Non-reactive handles.
    this.saveTimer = null;
    this.saveSeq = 0;
    this.smsPushTimer = null;
    this.panelOpener = null;
    this.modeListener = null;

    this.recentPatNums = getRecentPatNums(this.shellSiteID);
    this.boot();
  },

  mounted() {
    this.setupMode();
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onWindowKeydown);
      window.addEventListener('beforeunload', this.onBeforeUnload);
    }
    this.$emit('frame', this.frame);
  },

  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onWindowKeydown);
      window.removeEventListener('beforeunload', this.onBeforeUnload);
    }
    this.teardownMode();
    if (this.smsPushTimer) clearTimeout(this.smsPushTimer);
    // A pending arrangement is still saved (the dispatch outlives the component).
    this.flushSave();
  },

  methods: {
    // ---- classic side effects (Header.vue init()) -------------------------------------------------
    safeDispatch(type, payload) {
      try {
        return Promise.resolve(this.$store.dispatch(type, payload)).catch((err) => {
          console.error(`[AppShell] ${type} failed:`, err);
        });
      } catch (err) {
        console.error(`[AppShell] ${type} failed:`, err);
        return Promise.resolve();
      }
    },

    /**
     * Header.vue init(), minus what Layout's mixin already does (settings, employees, CASL rules):
     * the notifications listener (bell, Kanban dot, ANNOUNCEMENT bar), the empty-roster self-heal,
     * the chat-unread listener (Chat dot + alerts; idempotent in the store) and the
     * patient/employee "needs updating" listener the two watchers react to.
     */
    async boot() {
      const userID = this.shellUserID;
      const siteID = this.shellSiteID;
      await this.safeDispatch('notifications/getNotifications', { employeeID: userID, siteID });
      const patients = this.$store.state.customer.patients;
      if (!Array.isArray(patients) || patients.length === 0) this.refreshRoster();
      await this.safeDispatch('chat/checkIfNewChatMessagesAvailable', { currentUser: userID });
      this.safeDispatch('customer/checkifPatientOrEmployeeNeedsUpdating', { siteID });
    },

    /** Layout.vue playSound (Sidebar called it through $parent). Autoplay blocks are swallowed. */
    playAlertSound(sound) {
      if (!sound || typeof Audio === 'undefined') return;
      try {
        const p = new Audio(sound).play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (e) { /* no audio */ }
    },

    async refreshRoster() {
      if (this.patientsRefreshing) return false;
      this.patientsRefreshing = true;
      const ok = await refreshPatients(this);
      this.patientsRefreshing = false;
      if (ok) this.lastSyncedAt = Date.now();
      return ok;
    },

    // ---- viewport mode ----------------------------------------------------------------------------
    setupMode() {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      let desktop;
      let phone;
      try {
        desktop = window.matchMedia(DESKTOP_QUERY);
        phone = window.matchMedia(PHONE_QUERY);
      } catch (e) {
        return;
      }
      const onChange = () => {
        this.mode = shellModeFor(desktop.matches === true, phone.matches === true);
      };
      [desktop, phone].forEach((mql) => {
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else if (mql.addListener) mql.addListener(onChange);
      });
      this.modeListener = { queries: [desktop, phone], onChange };
      onChange();
    },

    teardownMode() {
      const l = this.modeListener;
      if (!l) return;
      l.queries.forEach((mql) => {
        if (mql.removeEventListener) mql.removeEventListener('change', l.onChange);
        else if (mql.removeListener) mql.removeListener(l.onChange);
      });
      this.modeListener = null;
    },

    // ---- popovers ---------------------------------------------------------------------------------
    openPanelAs(name) {
      if (!name) return;
      if (this.openPanel === name) return;
      // Remember what had focus so Escape can hand it back (not when swapping panels).
      if (!this.openPanel && typeof document !== 'undefined') this.panelOpener = document.activeElement;
      this.openPanel = name;
    },

    closePanel(opts) {
      if (!this.openPanel) return;
      this.openPanel = null;
      const opener = this.panelOpener;
      this.panelOpener = null;
      if (opts && opts.restoreFocus && opener && typeof opener.focus === 'function'
        && typeof document !== 'undefined' && opener !== document.body && document.contains(opener)) {
        try { opener.focus(); } catch (e) { /* detached */ }
      }
    },

    onTopBarOpen(name) {
      this.openPanelAs(name === 'search' && this.mode === 'phone' ? 'phoneSearch' : name);
    },

    // The phone ☰ (and the tab bar's More) open the drawer.
    onMenu() {
      this.openPanelAs('drawer');
    },

    // ×, the drawer's scrim, Escape: focus goes back to the ☰ / More tab that opened it.
    onDrawerClose() {
      if (this.openPanel === 'drawer') this.closePanel({ restoreFocus: true });
    },

    // Swap the drawer for the full-screen search (one panel at a time; the ☰ stays the opener for
    // Escape) and focus its input synchronously, inside this tap, so iOS raises the keyboard.
    onDrawerSearch() {
      this.openSearch();
    },

    onCloseFlyout() {
      if (this.openPanel === 'more') this.closePanel();
    },

    /**
     * Ctrl/Cmd+K → the search (phone: the full-screen sheet). Ignored while a modal is up, and
     * inside a modal / slide-out / rich-text editor (their own Ctrl+K, e.g. "insert link"); a plain
     * page text field does not block it — it is a global shortcut. Escape → close + focus back.
     */
    onWindowKeydown(e) {
      if (!e) return;
      if ((e.key === 'k' || e.key === 'K') && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
        if (this.ignoreShortcut(e)) return;
        e.preventDefault();
        this.openSearch();
        return;
      }
      if ((e.key === 'Escape' || e.key === 'Esc') && this.openPanel) {
        this.closePanel({ restoreFocus: true });
      }
    },

    ignoreShortcut(e) {
      if (e.defaultPrevented || this.switchSiteOpen) return true;
      if (typeof document !== 'undefined' && document.body && document.body.classList.contains('modal-open')) return true;
      const t = e.target;
      // A key on nothing focused targets <body>; anything that is not an element (window) is ignored.
      if (!t || t.nodeType !== 1) return false;
      if (this.$el && typeof this.$el.contains === 'function' && this.$el.contains(t)) return false;
      // The Tasks page composer is contenteditable but has no Ctrl+K of its own (it returns early on
      // modifier keys), so the search opens from it — the one editor exempted.
      if (typeof t.closest === 'function' && t.closest('[data-test="tasks-composer"]')) return false;
      if (t.isContentEditable) return true;
      return !!(typeof t.closest === 'function' && t.closest('.modal, .slideout-panel, [contenteditable="true"]'));
    },

    openSearch() {
      this.openPanelAs(this.mode === 'phone' ? 'phoneSearch' : 'search');
      const bar = this.$refs.topBar;
      if (bar && typeof bar.focusSearch === 'function') bar.focusSearch();
    },

    // ---- sidebar ----------------------------------------------------------------------------------
    // The row is a router-link (it navigates itself); the shell only closes what is open.
    onSelectApp() {
      this.closePanel();
    },

    onToggleCollapsed() {
      if (this.mode !== 'desktop') return;
      this.collapsedPref = !this.collapsedPref;
      writeCollapsed(browserStorage(), this.collapsedPref);
      if (this.openPanel === 'more') this.closePanel();
    },

    onReorder(next) {
      if (!next || !Array.isArray(next.pinned) || !Array.isArray(next.more)) return;
      this.applyLayout({ pinned: next.pinned, more: next.more });
    },

    onPin(id) {
      this.applyLayout(pinApp(this.layout, id));
    },

    onUnpin(id) {
      this.applyLayout(unpinApp(this.layout, id));
    },

    onResetLayout() {
      this.applyLayout(resolveLayout(null, this.visibleIds), { reset: true });
    },

    // ---- persistence (plan §5) ------------------------------------------------------------------
    /** Optimistic: draw the draft now, save SAVE_DELAY_MS after the last change. Admin + SA only. */
    applyLayout(next, opts) {
      if (!this.canEdit || !next) return;
      this.layoutDraft = {
        pinned: next.pinned.slice(),
        more: next.more.slice(),
        reset: !!(opts && opts.reset),
      };
      this.layoutDirty = true;
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        this.saveLayout();
      }, SAVE_DELAY_MS);
    },

    /** Save a pending arrangement now (destroy / beforeunload). Null when nothing is pending. */
    flushSave() {
      if (!this.saveTimer) return null;
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      return this.saveLayout();
    },

    onBeforeUnload() {
      // Best effort: the POST is fired; the browser may or may not let it finish.
      this.flushSave();
    },

    /**
     * POST /upsert-settings (one key, ON DUPLICATE KEY UPDATE) then re-read the settings. Failure
     * is detected BOTH ways the store reports it (a throw, or alert/errored — the TaskSettings.vue
     * pattern) and reverts to the stored arrangement.
     */
    async saveLayout() {
      const draft = this.layoutDraft;
      const siteID = this.shellSiteID;
      if (!this.canEdit || !draft || !siteID) return false;
      const value = serializeLayout(draft.reset ? draft : keepHiddenIds(draft, this.storedLayout, this.visibleIds));
      this.saveSeq += 1;
      const seq = this.saveSeq;
      const store = this.$store;
      let failed = false;
      try { store.dispatch('alert/setErrored', false); } catch (e) { /* no alert module */ }
      try {
        await store.dispatch('customer/upsertSettings', { settings: { [LAYOUT_SETTING]: value }, siteID });
        if (store.state.alert && store.state.alert.errored) failed = true;
      } catch (e) {
        failed = true;
      }
      if (failed) {
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.saveTimer = null;
        this.layoutDraft = null;
        this.layoutDirty = false;
        this.toast('error', 'Could not save the sidebar. Your change was undone.');
        return false;
      }
      try {
        await store.dispatch('customer/getSettings', { siteID });
      } catch (e) { /* the draft stays on screen; the next load reads the saved value */ }
      // Drop the draft only when nothing newer is pending AND the store now holds what we saved
      // (getSettings swallows its own errors — if it did not refresh, keep showing the draft).
      const row = this.settingsMap[LAYOUT_SETTING];
      if (seq === this.saveSeq && !this.saveTimer && row && row.PropertyValue === value) {
        this.layoutDraft = null;
        this.layoutDirty = false;
      }
      this.toast('success', 'Sidebar saved for this office', { timeout: 2500 });
      return true;
    },

    toast(kind, msg, opts) {
      try {
        if (this.$toast && typeof this.$toast[kind] === 'function') this.$toast[kind](msg, opts);
      } catch (e) { /* no toast plugin (specs) */ }
    },

    // ---- top bar actions (shellActions.js = Header.vue copies) -----------------------------------
    onPickPage(to) {
      this.closePanel();
      navigate(this, to);
    },
    onPickPatient(patient) {
      this.closePanel();
      gotoPatient(this, patient);
    },
    // A task from the search: the Tasks page's own deep link (/app/todo/<id>) opens it in the drawer.
    onPickTask(task) {
      if (!task || task.id === undefined || task.id === null) return;
      this.closePanel();
      navigate(this, { path: `/app/todo/${encodeURIComponent(task.id)}` }, 'the task');
    },
    onMyAccount() {
      this.closePanel();
      goToMyAccount(this);
    },
    onSettings() {
      this.closePanel();
      goToSettings(this);
    },
    onLogout() {
      this.closePanel();
      this.flushSave();
      logout(this);
    },
    onToggleMask() {
      this.closePanel();
      confirmToggleMask(this);
    },
    // b-modal (1040) sits under the popovers (1050): close them first.
    onSwitchSite() {
      this.closePanel();
      this.switchSiteOpen = true;
    },
    onSites(pos) {
      const p = pos || {};
      this.officeIndex = Number(p.index) || 0;
      this.officeCount = Number(p.count) || 0;
    },
    onClassicNav() {
      this.closePanel();
      this.$emit('nav-ui', 'old');
    },
    // The account menu / phone drawer's office switch: store, count, reload (uiSwitch.js).
    onOfficeSwitch(target) {
      this.closePanel();
      switchOfficeUi(target, { userKey: this.shellUserID, from: 'shell-menu' });
    },
    onOpenTasks() {
      this.closePanel();
      navigate(this, { path: '/app/todo' }, 'Tasks');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

/* ⛔ The shell is drawn in the design's face (Hanken Grotesk), but the app's global stylesheet sets
   `a, .dropdown-item, .btn { font-family: Montserrat }` (Bootstrap $font-family-base), which beats
   inheritance on every link: the sidebar rows are <router-link>s, so their labels rendered in
   Montserrat (Nilay, 2026-10-08). Set the face once at the root and make those elements inherit it.
   Covers every shell panel (sidebar, flyout, top bar, menus, tab bar, drawer). Pinned by
   cypress/node-tests/nav-shell-font.test.js. */
.app-shell {
  font-family: $fu-font-sans;
  -webkit-font-smoothing: antialiased;

  ::v-deep a,
  ::v-deep button,
  ::v-deep .btn,
  ::v-deep .dropdown-item {
    font-family: inherit;
  }
}

.app-shell__scrim {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $shell-z-scrim;
  background: transparent;
}

/* Header.vue .teamioAnnouncement, same look (red, white text, Log Out chip). */
.app-shell__announcement {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: $shell-z-announcement;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background-color: red;
  color: #fff;

  ::v-deep a {
    color: #fff;
    text-decoration: underline;
  }
}

.app-shell__announcement-logout {
  white-space: nowrap;
  margin-left: 20px;
  padding: 4px 12px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
  text-decoration: none !important;

  &:hover { background-color: rgba(255, 255, 255, 0.3); }
}
</style>
